import { ValuationsFormStep, ValuationsFormStepProps } from "../valuations-form-step";
import { ValuationsFormSchemaType } from "../valuations-form";
import { InlineTextButton } from "../../../components/buttons/text-button/inline-text-button";
import { ControlledTextInput } from "../../../components/forms/controlled-text-input.tsx";
import { ControlledSelect } from "../../../components/forms/controlled-select.tsx";
import { ManualAddressForm } from "./manual-address-form.tsx";
import { OptionalPropertyDetails } from "./optional-property-details.tsx";
import { ErrorMessage } from "../../../components/alerts/error-message";
import { useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { Path } from "react-hook-form";
import { FontStyles } from "../../../utils/font-styles.ts";
import { usePostcodeLookup } from "./use-postcode-lookup";
import { PROPERTY_TYPE_OPTIONS, getDisplayName } from "./property-options";

import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";

export const AddressDetailsStep = (props: ValuationsFormStepProps) => {
  // Track manual mode postcode error state
  const [manualModePostcodeError, setManualModePostcodeError] = useState<"NOT_FOUND" | "OUTSIDE_SERVICE_AREA" | null>(
    null,
  );
  const [
    postcode,
    addressMode,
    addressLine1,
    addressLine2,
    postTown,
    finalAddress,
    propertyType,
    bedrooms,
    sellingTimeframe,
    hasExistingSolicitor,
  ] = useWatch({
    control: props.form.form.control,
    name: [
      "postcode",
      "addressMode",
      "addressLine1",
      "addressLine2",
      "postTown",
      "finalAddress",
      "propertyType",
      "bedrooms",
      "sellingTimeframe",
      "hasExistingSolicitor",
    ],
  });

  const { addresses, isLoading, error } = usePostcodeLookup(postcode);

  const isManualEntry = addressMode === "manual";

  // Helper functions for bulk form operations
  const clearFormFields = useCallback(
    (fields: Path<ValuationsFormSchemaType>[]) => {
      fields.forEach((field) => props.form.form.setValue(field, ""));
    },
    [props.form.form],
  );

  const clearFormErrors = useCallback(
    (fields: Path<ValuationsFormSchemaType>[]) => {
      fields.forEach((field) => props.form.form.clearErrors(field));
    },
    [props.form.form],
  );

  // Clear fields when switching between lookup and manual modes
  useEffect(() => {
    if (addressMode === "lookup") {
      clearFormFields(["addressLine1", "addressLine2", "postTown", "finalAddress"]);
      clearFormErrors(["addressLine1", "postTown"]);
      setManualModePostcodeError(null); // Clear manual mode postcode error
      props.form.form.clearErrors("postcode"); // Clear built-in postcode field error from manual mode
    } else {
      clearFormFields(["finalAddress", "addressLine1", "addressLine2", "postTown"]);
      clearFormErrors(["finalAddress", "addressLine1", "postTown"]);
    }
  }, [addressMode, clearFormFields, clearFormErrors]);

  // Clear selected address when postcode changes and handle errors
  useEffect(() => {
    props.form.form.setValue("finalAddress", "");

    // Clear finalAddress error when addresses are loaded
    if (addresses.length > 0) {
      props.form.form.clearErrors("finalAddress");
    }
  }, [postcode, addresses.length, props.form.form]);

  // Reset solicitor selections when postcode changes
  // Only reset postcode-dependent fields, preserve user's own solicitor selection
  useEffect(() => {
    // Only reset the postcode-filtered solicitor list and related options
    // if user doesn't have their own solicitor
    if (hasExistingSolicitor !== "yes") {
      props.form.form.setValue("selectedSolicitors", []);
      props.form.form.setValue("selectedAlgorithm", "ActiveNearby");
      props.form.form.setValue("charteredOnly", false);
      props.form.form.clearErrors("selectedSolicitors");
    }
    // Note: hasExistingSolicitor and existingSolicitor are NOT reset
    // because they're not dependent on postcode
  }, [postcode, hasExistingSolicitor, props.form.form]);

  // Auto-populate finalAddress when in manual entry mode
  useEffect(() => {
    if (isManualEntry) {
      const addressParts = [addressLine1, addressLine2, postTown, postcode].filter(Boolean);
      const concatenatedAddress = addressParts.join(", ");
      props.form.form.setValue("finalAddress", concatenatedAddress);

      // Clear errors on finalAddress when user fills manual fields
      if (addressLine1 && postTown) {
        props.form.form.clearErrors("finalAddress");
      }
    }
  }, [isManualEntry, addressLine1, addressLine2, postTown, postcode, props.form.form]);

  const handleManualEntryToggle = useCallback(() => {
    props.form.form.setValue("addressMode", isManualEntry ? "lookup" : "manual");
  }, [isManualEntry, props.form.form]);

  const active = props.form.currentStep === props.index;

  // Build property details line if any optional fields are provided
  const propertyTypeDisplay = getDisplayName(PROPERTY_TYPE_OPTIONS, propertyType);
  const bedroomsDisplay = bedrooms ? `${bedrooms} ${bedrooms === "1" ? "bedroom" : "bedrooms"}` : null;
  const sellingTimeframeDisplay =
    sellingTimeframe && sellingTimeframe !== "Not sure" ? `Thinking about selling within ${sellingTimeframe}` : null;

  const propertyDetailsArray = [propertyTypeDisplay, bedroomsDisplay, sellingTimeframeDisplay].filter(Boolean);
  const propertyDetailsText = propertyDetailsArray.length > 0 ? propertyDetailsArray.join(" - ") : "";

  const text: string | string[] = active
    ? "Enter the address details of the property you would like valued."
    : [finalAddress ?? "Address not provided", propertyDetailsText].filter(Boolean);

  // Hide optional property details when postcode is outside service area (both modes)
  const hideControls = error === "OUTSIDE_SERVICE_AREA" || manualModePostcodeError === "OUTSIDE_SERVICE_AREA";

  return (
    <ValuationsFormStep {...props} text={text} hideControls={hideControls}>
      <div className="flex flex-col gap-4 md:gap-4.5">
        {!isManualEntry && (
          <div className="flex flex-col gap-4 md:gap-4.5">
            <ControlledTextInput
              control={props.form.form.control}
              name="postcode"
              showRequiredAsterisk
              onChange={(e) => {
                const hasError = !!props.form.form.formState.errors.postcode;
                props.form.form.setValue("postcode", e.target.value.toUpperCase(), {
                  shouldValidate: hasError,
                });
              }}
              state={error ? "error" : undefined}
              placeholder="Enter your postcode (e.g., SW1A 1AA)"
              label="Postcode"
              renderTrailingIcon={
                isLoading
                  ? () => <i className="fa-sharp fa-regular fa-spinner-third text-base animate-spin" />
                  : undefined
              }
            />
            {error === "OUTSIDE_SERVICE_AREA" && (
              <ErrorMessage>Sorry, this postcode is not covered by any ESPC solicitor estate agents.</ErrorMessage>
            )}
            {error === "NOT_FOUND" && (
              <ErrorMessage>
                Address not found. Please try again, or alternatively{" "}
                <InlineTextButton onClick={handleManualEntryToggle}>enter your address manually</InlineTextButton>.
              </ErrorMessage>
            )}
          </div>
        )}
        {!isManualEntry && addresses.length > 0 && (
          <>
            <ControlledSelect
              control={props.form.form.control}
              name="finalAddress"
              label="Select address"
              defaultText="Select an address"
              showRequiredAsterisk
              options={addresses.map((address) => ({ name: address, value: address }))}
              onValueChange={() => props.form.form.clearErrors("finalAddress")}
            />
            <div className={FontStyles["body-md"] + " text-light-slate"}>
              <span>
                Can't find your address?{" "}
                <InlineTextButton onClick={handleManualEntryToggle}>Enter your address manually</InlineTextButton>
              </span>
            </div>
          </>
        )}
        {isManualEntry && (
          <ManualAddressForm
            control={props.form.form.control}
            onBackToLookup={handleManualEntryToggle}
            onPostcodeError={setManualModePostcodeError}
          />
        )}
      </div>
      {error !== "OUTSIDE_SERVICE_AREA" && <OptionalPropertyDetails control={props.form.form.control} />}
    </ValuationsFormStep>
  );
};
